import { isResource } from '@medplum/core'
import type {
  Basic,
  Coding,
  Organization,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { compact } from 'lodash-es'
import { codeableConcept, hasCodeableConcept } from '#src/codeableConcept.ts'
import {
  BASIC_CODE_SYSTEM,
  NOTE_LABEL_SYSTEM_MANAGED_ID_SYSTEM,
  NOTE_LABEL_TAG_SYSTEM,
} from '#src/codeSystems.ts'
import { Tag } from '#src/decorators.ts'
import { Label } from '#src/models/label.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

export class NoteLabel extends Label {
  static isInstance(value: unknown): value is Basic {
    return (
      isResource(value) &&
      isOfType(value, 'Basic') &&
      hasCodeableConcept(value.code, codeableConcept(NoteLabel.code))
    )
  }

  constructor({
    name,
    color,
    author,
  }: {
    name: string
    color: string
    author: Reference<Practitioner | Organization>
  }) {
    super({
      code: codeableConcept(NoteLabel.code),
      name,
      color,
      author,
    })
  }

  @Tag({
    system: NOTE_LABEL_SYSTEM_MANAGED_ID_SYSTEM,
  })
  _systemManagedId?: Coding

  get isSystemManaged(): boolean {
    return !!this._systemManagedId?.code
  }

  static isValidReferenceString(
    referenceString: unknown
  ): referenceString is NoteLabel['referenceString'] {
    return (
      typeof referenceString === 'string' &&
      referenceString.startsWith('Basic/')
    )
  }

  /**
   * Base FHIR search query for this resource — extended/modified at call sites.
   * Changing this affects every caller. Grep `baseSearchQuery` for impact before editing.
   */
  static baseSearchQuery(): QueryTypes {
    return {
      code: NoteLabel.code,
      _tag: Label.status.active,
      _sort: '-_lastUpdated',
      _count: '1000',
    }
  }

  static queryBySystemManagedId(systemManagedId: string): QueryTypes {
    return {
      ...this.baseSearchQuery(),
      _tag: {
        system: NOTE_LABEL_SYSTEM_MANAGED_ID_SYSTEM,
        code: systemManagedId,
      },
    }
  }

  static code = {
    system: BASIC_CODE_SYSTEM,
    code: 'note-label',
    display: 'Note Label',
  }
}

/**
 * Read the `NoteLabel` reference strings stored in a `meta.tag` coding set (the
 * `NOTE_LABEL_TAG_SYSTEM` codings managed by an `@Tag` field). Shared by every
 * composition that surfaces note labels so the read contract has one home.
 */
export function readNoteLabelReferences(
  codings: Coding[] | undefined
): NoteLabel['referenceString'][] {
  return compact(
    codings
      ?.map((l) => l.code)
      .filter((c) => NoteLabel.isValidReferenceString(c))
  )
}

/**
 * Serialize `NoteLabel` reference strings to `meta.tag` codings, or `undefined`
 * when empty so the `@Tag` field clears cleanly.
 */
export function noteLabelTagCodings(
  labels: NoteLabel['referenceString'][] | undefined
): Coding[] | undefined {
  if (!labels || labels.length === 0) {
    return undefined
  }
  return labels.map((l) => ({ code: l, system: NOTE_LABEL_TAG_SYSTEM }))
}
